import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faBellSlash } from "@fortawesome/pro-solid-svg-icons/faBellSlash";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form, { FormInstance } from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import Tooltip from "antd/es/tooltip";
import Paragraph from "antd/es/typography/Paragraph";
import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import EmailSchedule from "@mapmycustomers/shared/types/email/EmailSchedule";
import {
  EntityType,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";
import stringHash from "@mapmycustomers/shared/util/hash/stringHash";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import {
  DatePicker,
  FormItem,
  Modal,
  Popconfirm,
  TextAreaWithMentions,
  TextField,
} from "@mapmycustomers/ui";

import ActivityNameField from "@app/component/activity/ActivityNameField";
import LockedActivityType from "@app/component/activity/LockedFormFields/LockedActivityType";
import LockedAssignee from "@app/component/activity/LockedFormFields/LockedAssignee";
import useMentionsValidation from "@app/component/activity/utils/useMentionsValidation";
import Chip from "@app/component/Chip";
import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import ActivityCompleted from "@app/component/createEditEntity/Activity/component/ActivityCompleted";
import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import getActivityPayloadFromCreationForm from "@app/component/createEditEntity/Activity/utils/getActivityPayloadFromCreationForm";
import getFormInitialValues from "@app/component/createEditEntity/Activity/utils/getFormInitialValues";
import { useIsRecurrentValueChangeHandler } from "@app/component/createEditEntity/Activity/utils/useIsRecurrentValueChangeHandler";
import { useRecurIntervalTypeValueChangeHandler } from "@app/component/createEditEntity/Activity/utils/useRecurIntervalTypeValueChangeHandler";
import EmailContext from "@app/component/email/creation/EmailContext";
import FormFields from "@app/component/FormFields";
import convertFieldToLayoutSchemaField from "@app/component/FormFields/utils/convertFieldToLayoutSchemaField";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import Checkbox from "@app/component/input/Checkbox";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import { getActivityTypes, getEmailActivityType } from "@app/store/activity";
import { ActivityCreationPayloadWithCustomFields } from "@app/store/api/ApiService";
import {
  doesOrganizationSupportMultiContactActivities,
  getCurrentUser,
  isCurrentUserManager,
  isCurrentUserOwner,
} from "@app/store/iam";
import {
  getAllTeamsOfEntireOrganization,
  getTeams,
  getUsersOfEntireOrganization,
} from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { getDefaultActivityEmailLayout } from "@app/util/email/getActivityEmailLayouts";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import getActivityPersonField from "@app/util/fieldModel/getActivityPersonField";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useFieldValidation from "@app/util/hook/useFieldValidation";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";
import { getActivityVisibilityNote } from "@app/util/ui";

import EmailScheduleSendPopover from "../../EmailScheduleSendPopover";
import EmailSendAndCreateActivitiesButton from "../EmailSendAndCreateActivitiesButton";
import EmailSubjectView from "../EmailSubjectView";

import styles from "./BaseEmailActivitiesCreateModal.module.scss";
import Files from "./Files";
import messages from "./messages";

export interface BaseEmailActivitiesCreateModalProps {
  formLayout: FormLayout;
  onBack: () => void;
  onCreate: (args: {
    activity: ActivityCreationPayloadWithCustomFields;
    fieldsValueValidateCallback: (customFieldErrors: CustomFieldError[]) => void;
    schedule?: EmailSchedule;
  }) => void;
  onFormInitialize?: (form: FormInstance<FormValues>) => void;
  onFormLayoutChange?: (layout: FormLayout) => void;
  onHide: () => void;
  recipientCount: number;
  recipientType: EntityTypesSupportedByEmailFeature;
  renderCompanyField: (props: ConditionalFormFieldRenderProps) => ReactElement;
  renderDealField: (props: ConditionalFormFieldRenderProps) => ReactElement;
  renderPersonField: (props: ConditionalFormFieldRenderProps) => ReactElement;
  schemaFields: LayoutSchemaField[];
  sendAndCreateDisabled?: boolean;
  sendAndCreateTooltipTitle?: string;
}

const BaseEmailActivitiesCreateModal = ({
  activityTypes,
  allTeams,
  currentUser,
  emailActivityType,
  formLayout,
  isManager,
  isMultiContactEnabled,
  isOwner,
  onBack,
  onCreate,
  onFormInitialize,
  onFormLayoutChange,
  onHide,
  recipientCount,
  recipientType,
  renderCompanyField,
  renderDealField,
  renderPersonField,
  schemaFields,
  sendAndCreateDisabled: sendAndCreateDisabledProp,
  sendAndCreateTooltipTitle: sendAndCreateTooltipTitleProp,
  teams,
  users,
}: BaseEmailActivitiesCreateModalProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const [form] = useForm<FormValues>();
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [schedule, setSchedule] = useState<EmailSchedule | undefined>();
  const { attachedFiles, dynamicVariables, sending, subject, subjectContainsDynamicVariables } =
    useNonNullableContext(EmailContext);

  const isBulkEmail = recipientCount > 0;

  const initialValues = {
    ...getFormInitialValues(
      { completed: true, name: subject },
      currentUser,
      emailActivityType?.id,
      false,
      ActivityVisibility.PRIVATE,
      true
    ),
  };

  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();

  const isRecurrentValueChangeHandler = useIsRecurrentValueChangeHandler(form);
  const recurIntervalTypeValueChangeHandler = useRecurIntervalTypeValueChangeHandler(form);

  const visibilityOptions = useActivityVisibilityOptions(isOwner, isManager);

  const hasRequiredUniqueFields = useMemo(
    () =>
      schemaFields.some((schemaField) => {
        const field = activityFieldModel.getByPlatformName(schemaField.field);
        return schemaField.required && field?.isUnique;
      }),
    [schemaFields]
  );

  const sendAndCreateDisabled =
    sending || sendAndCreateDisabledProp || (isBulkEmail && hasRequiredUniqueFields);

  const sendAndCreateTooltipTitle: string | undefined = useMemo(() => {
    if (sendAndCreateTooltipTitleProp) {
      return sendAndCreateTooltipTitleProp;
    }

    if (isBulkEmail && hasRequiredUniqueFields) {
      return intl.formatMessage(messages.uniqueFieldRequiredTooltip);
    }

    return undefined;
  }, [intl, isBulkEmail, hasRequiredUniqueFields, sendAndCreateTooltipTitleProp]);

  const defaultAddedFields = useMemo(() => {
    const result = [];
    if (recipientType) {
      const visibleFieldNames = new Set(
        getDefaultActivityEmailLayout()?.schema.map(({ field }) => field) ?? []
      );

      const fieldModel = getFieldModelByEntityType(EntityType.ACTIVITY);
      if (attachedFiles.length > 0 && !visibleFieldNames.has("files")) {
        const field = fieldModel.getByName(ActivityFieldName.FILES);
        if (field) {
          result.push(convertFieldToLayoutSchemaField(field));
        }
      }
      if (recipientType === EntityType.COMPANY && !visibleFieldNames.has("accountId")) {
        const field = fieldModel.getByName(ActivityFieldName.COMPANY);
        if (field) {
          result.push(convertFieldToLayoutSchemaField(field));
        }
      }
      if (recipientType === EntityType.PERSON && !visibleFieldNames.has("contactId")) {
        const field = getActivityPersonField();
        if (field) {
          result.push(convertFieldToLayoutSchemaField(field));
        }
      }
    }
    return result;
  }, [attachedFiles, recipientType]);

  const [, allowedUserEmails, handleAllMentionedUsersHaveAccessToActivity] = useMentionsValidation(
    currentUser.id,
    users
  );

  const handleActivityChange = useDynamicCallback((values: FormValues, allValues: FormValues) => {
    if (allValues.endAt && allValues.startAt && allValues.endAt < allValues.startAt) {
      form.setFieldsValue({ endAt: allValues.startAt });
    }
  });

  const handleIsMentionDisabled = useDynamicCallback(
    (user: User) => !allowedUserEmails?.includes(user.username)
  );

  const handleValuesChange = useDynamicCallback((changedValues: FormValues, values: FormValues) => {
    [
      isRecurrentValueChangeHandler,
      recurIntervalTypeValueChangeHandler,
      handleAllMentionedUsersHaveAccessToActivity,
      handleActivityChange,
    ].forEach((fn) => fn(changedValues, values));
  });

  const handleFieldValidationErrors = useFieldValidation(form, handleFinishFail);

  useEffect(() => {
    onFormInitialize?.(form);
  }, [form, onFormInitialize]);

  useEffect(() => {
    form.setFieldValue(
      "files",
      attachedFiles.map((attachedFile) => attachedFile.file)
    );
  }, [form, attachedFiles]);

  useEffect(() => {
    const uniqueFields = activityFieldModel.fields.filter((field) => field?.isUnique);
    uniqueFields.forEach((field) => {
      if (isCustomField(field)) {
        form.setFieldValue(["customFields", field.customFieldData.esKey], undefined);
      } else {
        form.setFieldValue(field.name, undefined);
      }
    });
  }, [form]);

  const handleFormFinish = useDynamicCallback(() => {
    const values = form.getFieldsValue();
    const { customFields, ...activity } = getActivityPayloadFromCreationForm(
      {
        ...values,
        activityTypeId: emailActivityType?.id,
        assignee: currentUser,
        name: subjectContainsDynamicVariables ? subject : values.name,
      },
      activityTypes,
      isMultiContactEnabled
    );

    onCreate({
      activity: {
        ...activity,
        ...(Object.values(values.customFields ?? {}).some(isNotEmpty)
          ? {
              customFields: [
                {
                  customFieldData:
                    Object.values(values.customFields ?? {})
                      .filter(isDefined)
                      .map((cf) => ({
                        customField: { id: cf.customField.id },
                        value: cf.value,
                      })) ?? [],
                },
              ],
            }
          : {}),
      },
      fieldsValueValidateCallback: handleFieldValidationErrors,
      ...(scheduleEmail && schedule ? { schedule } : {}),
    });
  });

  const handleScheduleChange = useCallback((value: Date | undefined) => {
    if (!value) {
      setSchedule(undefined);
    }
  }, []);

  return (
    <Modal
      actions={
        <Chip icon={<FontAwesomeIcon className={styles.icon} icon={faBellSlash} />} type="disabled">
          {intl.formatMessage(messages.notificationsDisabled)}
        </Chip>
      }
      className={styles.modal}
      closeIcon={
        <span onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            cancelText={intl.formatMessage(messages.closeConfirmationNo)}
            danger
            description={intl.formatMessage(messages.closeConfirmationDescription)}
            okText={intl.formatMessage(messages.closeConfirmationYes)}
            onConfirm={onHide}
            overlayClassName={styles.closeConfirmation}
            title
          >
            <FontAwesomeIcon icon={faTimes} />
          </Popconfirm>
        </span>
      }
      footer={null}
      maskClosable={false}
      onCancel={onHide}
      open
      title={
        <>
          <Tooltip title={intl.formatMessage(messages.goBack)}>
            <FontAwesomeIcon className={styles.backLink} icon={faChevronLeft} onClick={onBack} />
          </Tooltip>
          <span>{intl.formatMessage(messages.title, { count: recipientCount })}</span>
          {activityLayoutModel.layouts.length > 1 && (
            <FormLayoutSelector
              entityType={EntityType.ACTIVITY}
              layout={formLayout}
              onChange={onFormLayoutChange}
            />
          )}
        </>
      }
      width="42rem"
    >
      <div className={styles.formWrapper} ref={formFieldsRef}>
        <Form<FormValues>
          className={styles.form}
          form={form}
          initialValues={initialValues}
          layout="vertical"
          onFinish={handleFormFinish}
          onFinishFailed={handleFinishFail}
          onValuesChange={handleValuesChange}
          preserve
        >
          <div className={styles.container}>
            <div className={styles.note}>{intl.formatMessage(messages.note)}</div>

            {formLayout && (
              <FormFields
                defaultAddedFields={defaultAddedFields}
                disableUniqueFields={isBulkEmail}
                entityType={EntityType.ACTIVITY}
                fileComponent={Files}
                layout={formLayout}
              >
                <ConditionalFormField feature={FieldFeature.ACTIVITY_TYPE_FIELD}>
                  {emailActivityType && (
                    <LockedActivityType activityType={emailActivityType} isAutoEmail />
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.ASSIGNEE}>
                  {({ label, required }: ConditionalFormFieldRenderProps) => (
                    <LockedAssignee assignee={currentUser} label={label} required={required} />
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.NAME}>
                  {subjectContainsDynamicVariables ? (
                    ({ label, required }: ConditionalFormFieldRenderProps) => (
                      <div className={styles.disabledActivityNameContainer}>
                        <TextField
                          caption={intl.formatMessage(
                            messages.activityNameDisabledBecauseItUsesDynamicVariable
                          )}
                          label={label}
                          locked
                          required={required}
                        />
                        <Paragraph
                          className={styles.emailSubjectViewParagraph}
                          ellipsis={{
                            expandable: false,
                            rows: 1,
                            tooltip: {
                              placement: "right",
                            },
                          }}
                        >
                          <EmailSubjectView
                            dynamicVariables={dynamicVariables}
                            greyed
                            subject={subject}
                          />
                        </Paragraph>
                      </div>
                    )
                  ) : (
                    <ActivityNameField
                      activityTypes={activityTypes}
                      title={intl.formatMessage(messages.activityName)}
                    />
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.COMPANY}>
                  {(props: ConditionalFormFieldRenderProps) => renderCompanyField(props)}
                </ConditionalFormField>

                <ConditionalFormField
                  fieldName={[ActivityFieldName.PERSON, ActivityFieldName.PEOPLE]}
                >
                  {(props: ConditionalFormFieldRenderProps) =>
                    renderPersonField({
                      ...props,
                      // Email is 1:1 — keep the singular "Person" label even when the
                      // org-level layout swapped in the multi-contact PEOPLE field.
                      // no changes to Email in v1
                      label: intl.formatMessage({
                        id: "email.creation.baseActivitiesCreateModal.field.contact",
                        defaultMessage: "Person",
                        description: "Person field of activity",
                      }),
                    })
                  }
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.DEAL}>
                  {(props: ConditionalFormFieldRenderProps) => renderDealField(props)}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.NOTE}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <FormItem
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.note !== curValues.note ||
                        prevValues.visibility !== curValues.visibility
                      }
                    >
                      {({ getFieldValue }) => (
                        <FormItem
                          label={label}
                          name={ActivityFieldName.NOTE}
                          required={required}
                          rules={[{ required }]}
                        >
                          <TextAreaWithMentions
                            allUsers={users}
                            disabled={disabled}
                            emptyTextForWithSkippedUsers={intl.formatMessage(
                              messages.emptyTextForWithSkippedUsers
                            )}
                            hideDisabledUsers
                            isMentionDisabled={handleIsMentionDisabled}
                            key={
                              getFieldValue(ActivityFieldName.VISIBILITY) +
                              stringHash(allowedUserEmails?.join() ?? "")
                            }
                            maxLength={Number.MAX_SAFE_INTEGER}
                            teams={allTeams}
                          />
                        </FormItem>
                      )}
                    </FormItem>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.VISIBILITY}>
                  {({ disabled, required }: ConditionalFormFieldRenderProps) => (
                    <FormItem
                      label={intl.formatMessage(messages.visibility)}
                      required={required}
                      shouldUpdate={(prev, next) =>
                        prev.visibility !== next.visibility || prev.teamIds !== next.teamIds
                      }
                    >
                      {({ getFieldValue, setFieldsValue }) => (
                        <VisibilitySelectField<ActivityVisibility>
                          disabled={disabled}
                          noteGetter={getActivityVisibilityNote}
                          onChange={(visibility: ActivityVisibility) =>
                            setFieldsValue({ visibility })
                          }
                          onTeamIdsChange={(teamIds: Team["id"][]) => setFieldsValue({ teamIds })}
                          options={visibilityOptions}
                          teamIds={getFieldValue("teamIds")}
                          teams={teams}
                          teamsRenderer={(node) => (
                            <FormItem
                              name="teamIds"
                              noStyle
                              rules={[
                                {
                                  message: intl.formatMessage(messages.teamsRequired),
                                  required: true,
                                },
                              ]}
                            >
                              {node}
                            </FormItem>
                          )}
                          value={getFieldValue(ActivityFieldName.VISIBILITY)}
                          visibilityRenderer={(node) => (
                            <FormItem name={ActivityFieldName.VISIBILITY} noStyle>
                              {node}
                            </FormItem>
                          )}
                        />
                      )}
                    </FormItem>
                  )}
                </ConditionalFormField>
              </FormFields>
            )}
          </div>
          <div className={styles.footer}>
            <div>
              <div className={styles.activityDateSchedule}>
                <Checkbox onChange={setScheduleEmail} value={scheduleEmail}>
                  {intl.formatMessage(messages.scheduleEmailChechbox)}
                </Checkbox>
                <div className={styles.footerInfo}>
                  <div className={styles.clockIcon}>
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div className={styles.dateScheduleInfo}>
                    {scheduleEmail ? (
                      <EmailScheduleSendPopover onChange={setSchedule} value={schedule}>
                        <DatePicker
                          allowClear
                          className={styles.datePicker}
                          format={intl.formatMessage(messages.scheduleDateTimeFormat)}
                          onChange={handleScheduleChange}
                          open={false}
                          placeholder={intl.formatMessage(messages.schedulePlaceholder)}
                          size="middle"
                          suffixIcon={<></>}
                          value={schedule ? parseApiDate(schedule.sendAt) : null}
                        />
                      </EmailScheduleSendPopover>
                    ) : (
                      <div className={cn(styles.footerInfoText, styles.footerInfoTextDateTime)}>
                        {intl.formatMessage(messages.activityDateTimeDescription)}
                      </div>
                    )}
                    <div className={styles.footerInfoText}>
                      {intl.formatMessage(messages.doesNotRepeat)}
                    </div>
                  </div>
                </div>
              </div>
              <ActivityCompleted creating disabled form={form} />
            </div>
            <div>
              <EmailSendAndCreateActivitiesButton
                disabled={sendAndCreateDisabled}
                loading={sending}
                onClick={form.submit}
                recipientCount={recipientCount}
                schedule={scheduleEmail ? schedule : undefined}
                tooltipTitle={sendAndCreateTooltipTitle}
              />
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  allTeams: getAllTeamsOfEntireOrganization(state),
  currentUser: getCurrentUser(state)!,
  emailActivityType: getEmailActivityType(state),
  isManager: isCurrentUserManager(state),
  isMultiContactEnabled: doesOrganizationSupportMultiContactActivities(state),
  isOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
  users: getUsersOfEntireOrganization(state),
});

export default connect(mapStateToProps)(BaseEmailActivitiesCreateModal);
